import React, { useState } from 'react';
import { Lock, ShieldCheck, ArrowRight, AlertCircle } from 'lucide-react';

// SET TO false WHEN YOU WANT TO REMOVE THE PASSWORD PROTECTION FOR EVERYONE
export const TESTER_LOCK_ENABLED = true;

const REQUIRED_USER = 'infraEdged@1298';
const REQUIRED_PASS = 'infraEdgedPass@123';
const STORAGE_KEY = 'infraedge_tester_authenticated_v1';

const TesterAccessGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (!TESTER_LOCK_ENABLED) return true;
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (!TESTER_LOCK_ENABLED || isAuthenticated) {
    return <>{children}</>;
  }

  const handleUnlock = (e) => {
    e.preventDefault();
    setError('');

    if (username.trim() === REQUIRED_USER && password === REQUIRED_PASS) {
      try {
        localStorage.setItem(STORAGE_KEY, 'true');
      } catch (err) {}
      setIsAuthenticated(true);
    } else {
      setError('Invalid username or password. Please try again.');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 999999
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          background: '#ffffff',
          borderRadius: '24px',
          padding: '40px 36px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.45)',
          boxSizing: 'border-box',
          textAlign: 'center'
        }}
      >
        {/* Brand Dots Logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '2.5px',
              width: '18px',
              height: '18px'
            }}
          >
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: '#dc2626'
                }}
              />
            ))}
          </div>
          <span style={{ fontSize: '15px', fontWeight: 900, letterSpacing: '0.08em', color: '#0f172a' }}>
            INFRAEDGE
          </span>
        </div>

        {/* Lock Icon */}
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: '#fef2f2',
            border: '1.5px solid #fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            color: '#dc2626'
          }}
        >
          <Lock size={26} />
        </div>

        <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
          Restricted Staging Access
        </h2>
        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 28px', lineHeight: 1.5 }}>
          This environment is currently reserved for authorized testers only. Please authenticate below.
        </p>

        {error && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '12px 14px',
              color: '#dc2626',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '20px',
              textAlign: 'left'
            }}
          >
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleUnlock} style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
              Tester Username
            </label>
            <input
              type="text"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="e.g. infraEdged@1298"
              required
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '10px',
                border: '1.5px solid #cbd5e1',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
                color: '#0f172a'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
              Tester Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter tester password"
              required
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '10px',
                border: '1.5px solid #cbd5e1',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
                color: '#0f172a'
              }}
            />
          </div>

          <button
            type="submit"
            style={{
              marginTop: '8px',
              background: '#dc2626',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              padding: '14px',
              fontSize: '14.5px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(220, 38, 38, 0.35)',
              transition: 'background 0.2s'
            }}
          >
            <span>Access Testing Site</span>
            <ArrowRight size={16} />
          </button>
        </form>

        <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8' }}>
          <ShieldCheck size={14} color="#16a34a" />
          <span>InfraEdge Red Light Therapy • Staging 2026</span>
        </div>
      </div>
    </div>
  );
};

export default TesterAccessGate;
